/**
 * Calibration maths — anchor pairs, the similarity solve, the measure-scale
 * tool and the composed ⇄ registration transform conversion
 * (spec 2026-08-22-03 §1, §2, §6).
 *
 * A registration places a *source* (a raster crop, another asset's geometry)
 * into a *target frame*. The plan editor works in the frame asset's
 * coordinates, so what it manipulates is the **composed** transform the scene
 * endpoint hands out (`source local → edited frame`). The registration row
 * itself only stores its own hop, hence `registrationTransformFor`.
 *
 * Pure TypeScript — no React, no DOM, unit-tested with vitest.
 */
import {
    applyTransform,
    composeTransform,
    dist,
    invertTransform,
    toDeg,
    toRad,
    type BBox,
    type Point,
    type Transform,
} from './geometry'

/**
 * One point correspondence. `source` is expressed in the calibrated
 * registration's own source coordinates, `target` in the edited frame — the
 * exact shape stored in `provenance.pairs` (spec 2026-07-27-08 §5).
 */
export interface AnchorPair {
    source: Point
    target: Point
}

/** Normalizes an angle into (-180, 180], so a transform reads sanely. */
export function normalizeAngle(deg: number): number {
    let d = deg % 360
    if (d > 180) d -= 360
    if (d <= -180) d += 360
    return d === 0 ? 0 : d
}

/** `to ∘ from⁻¹` — the frame-space move that takes `from` onto `to`. */
export function deltaTransform(from: Transform, to: Transform): Transform {
    const d = composeTransform(to, invertTransform(from))
    return { ...d, rotation_deg: normalizeAngle(d.rotation_deg) }
}

/**
 * Least-squares 2D similarity (translation + rotation + uniform scale) mapping
 * every `source` onto its `target` — the closed-form Umeyama/Procrustes
 * solution, no linear-algebra dependency needed.
 *
 * - **0 pairs** → `current`, untouched.
 * - **1 pair** → pure translation: rotation and scale are kept, the transform
 *   is slid so the single source lands on its target (exp's `applyPairs`).
 * - **2 pairs** → the similarity is exactly determined, so the least-squares
 *   solution *is* the exact one (this is the extension to rotation the exp
 *   editor never had: it only solved scale + offset).
 * - **3+ pairs** → the best similarity in the least-squares sense.
 *
 * Degenerate inputs (every source at the same point, a collapsing scale) fall
 * back to translating with the current rotation/scale rather than producing a
 * singular transform.
 */
export function solveSimilarity(pairs: AnchorPair[], current: Transform): Transform {
    if (!pairs.length) return { ...current }
    const scale = current.scale || 1

    if (pairs.length === 1) {
        const { source, target } = pairs[0]
        const rotated = applyTransform({ tx: 0, ty: 0, rotation_deg: current.rotation_deg, scale }, source)
        return {
            tx: target.x - rotated.x,
            ty: target.y - rotated.y,
            rotation_deg: normalizeAngle(current.rotation_deg),
            scale,
        }
    }

    const n = pairs.length
    const ms = { x: 0, y: 0 }
    const mt = { x: 0, y: 0 }
    for (const p of pairs) {
        ms.x += p.source.x / n
        ms.y += p.source.y / n
        mt.x += p.target.x / n
        mt.y += p.target.y / n
    }

    // a = Σ s̃·t̃ (dot), b = Σ s̃×t̃ (cross), varS = Σ|s̃|².
    // Maximizing Σ (c·R(θ)·s̃)·t̃ gives θ = atan2(b, a) and c = hypot(a,b)/varS
    // for the Y-down / clockwise-positive rotation matrix this codebase uses.
    let a = 0
    let b = 0
    let varS = 0
    for (const p of pairs) {
        const sx = p.source.x - ms.x
        const sy = p.source.y - ms.y
        const tx = p.target.x - mt.x
        const ty = p.target.y - mt.y
        a += sx * tx + sy * ty
        b += sx * ty - sy * tx
        varS += sx * sx + sy * sy
    }

    if (varS < 1e-12) {
        // Every source point coincides: nothing but a translation is knowable.
        const rotated = applyTransform({ tx: 0, ty: 0, rotation_deg: current.rotation_deg, scale }, ms)
        return {
            tx: mt.x - rotated.x,
            ty: mt.y - rotated.y,
            rotation_deg: normalizeAngle(current.rotation_deg),
            scale,
        }
    }

    const theta = Math.atan2(b, a)
    const c = Math.hypot(a, b) / varS
    const solvedScale = c > 1e-9 ? c : scale
    const cos = Math.cos(theta)
    const sin = Math.sin(theta)
    return {
        tx: mt.x - solvedScale * (ms.x * cos - ms.y * sin),
        ty: mt.y - solvedScale * (ms.x * sin + ms.y * cos),
        rotation_deg: normalizeAngle(toDeg(theta)),
        scale: solvedScale,
    }
}

/**
 * The measure-scale tool (exp's "📏"): two points clicked on the placed layer
 * and their true distance in millimetres. The layer is rescaled **about the
 * segment's midpoint**, so the measured stretch stays centred where the user
 * was looking (spec §2).
 */
export function measureScaleTransform(
    current: Transform,
    a: Point,
    b: Point,
    realDistanceMm: number,
): Transform {
    const measured = dist(a, b)
    if (!(measured > 1e-9) || !(realDistanceMm > 0)) return { ...current }
    const f = realDistanceMm / measured
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
    // S(mid, f) ∘ current: p ↦ mid + f·(current(p) − mid)
    return {
        tx: f * current.tx + mid.x * (1 - f),
        ty: f * current.ty + mid.y * (1 - f),
        rotation_deg: normalizeAngle(current.rotation_deg),
        scale: (current.scale || 1) * f,
    }
}

/**
 * Turns a *composed* transform (source local → edited frame) back into the
 * transform of the registration row itself.
 *
 * The chain resolves recursively — a unit's plan registered into the unit,
 * itself registered into the floor — so `composed = parent ∘ own`. Knowing the
 * registration's stored `own` transform recovers `parent`, and the new own
 * transform is `parent⁻¹ ∘ composedAfter`. When the registration targets the
 * edited frame directly (the common case) `parent` is the identity and the
 * result is `composedAfter` itself.
 */
export function registrationTransformFor(
    composedBefore: Transform,
    ownBefore: Transform,
    composedAfter: Transform,
): Transform {
    const parent = composeTransform(composedBefore, invertTransform(ownBefore))
    const own = composeTransform(invertTransform(parent), composedAfter)
    return { ...own, rotation_deg: normalizeAngle(own.rotation_deg) }
}

/**
 * A translation + uniform-scale fit of `source` into `target`, used when an
 * imported floorplan joins a frame that already holds geometry (spec §7). With
 * no target to fit against the transform is the identity: the import is already
 * in millimetres, and guessing a scale would be worse than showing it as drawn.
 */
export function autoFitTransform(source: BBox | null, target: BBox | null, margin = 0.92): Transform {
    if (!source || !target) return { tx: 0, ty: 0, rotation_deg: 0, scale: 1 }
    const sw = Math.max(source.x1 - source.x0, 1e-6)
    const sh = Math.max(source.y1 - source.y0, 1e-6)
    const tw = Math.max(target.x1 - target.x0, 1e-6)
    const th = Math.max(target.y1 - target.y0, 1e-6)
    const scale = Math.min(tw / sw, th / sh) * margin
    const sc = { x: (source.x0 + source.x1) / 2, y: (source.y0 + source.y1) / 2 }
    const tc = { x: (target.x0 + target.x1) / 2, y: (target.y0 + target.y1) / 2 }
    return { tx: tc.x - sc.x * scale, ty: tc.y - sc.y * scale, rotation_deg: 0, scale }
}

/**
 * A rotation of `deg` about `pivot`, composed on top of `current` — what the
 * layer rotate handle produces (spec §6).
 */
export function rotateAbout(current: Transform, pivot: Point, deg: number): Transform {
    const r = toRad(deg)
    const cos = Math.cos(r)
    const sin = Math.sin(r)
    const rotate: Transform = {
        tx: pivot.x - (pivot.x * cos - pivot.y * sin),
        ty: pivot.y - (pivot.x * sin + pivot.y * cos),
        rotation_deg: deg,
        scale: 1,
    }
    const next = composeTransform(rotate, current)
    return { ...next, rotation_deg: normalizeAngle(next.rotation_deg) }
}

/** A translation of (dx, dy) composed on top of `current`. */
export function translateBy(current: Transform, dx: number, dy: number): Transform {
    return { ...current, tx: current.tx + dx, ty: current.ty + dy }
}

/* ------------------------------------------- calibration (spec 2026-08-25-04 §3) */

/**
 * How many real millimetres one plan unit is worth — the wire shape of
 * `FrameCalibration` (spec 2026-08-25-03 §3).
 *
 * Calibration is **metadata, not a transform**. It never moves anything: the
 * geometry stays glued to the plan it was traced over and every readout
 * multiplies through `mm_per_unit` at read time. That is what makes the real
 * workflow — trace first, measure later — safe.
 */
export interface FrameCalibration {
    mm_per_unit: number
    /**
     * `auto` is the `floorplan_calibrate` job's own reading of the plan's
     * printed cotes (spec 2026-09-05-07 §4). It carries a real `measure` — the
     * surviving annotation closest to the median — so it draws on the stage
     * exactly like a hand-measured one.
     */
    method?: 'measure' | 'anchor_pairs' | 'manual' | 'auto'
    measure?: { a: Point; b: Point; distance_mm: number }
    /** Optional ends mirror the wire schema; the editor always writes both. */
    pairs?: { source?: Point; target?: Point }[]
    /** The calibrate job's verdict on an `auto` scale (spec 2026-09-30-02 §4). */
    confidence?: 'high' | 'low'
    /** A user accepted a `low` auto scale the job only proposed. */
    accepted?: boolean
    /** N of the "1:N" ratio the calibration came from (spec 2026-10-16-06). */
    stated_scale?: number
}

/**
 * A typed drawing scale "1:N" as a calibration (spec 2026-10-16-06).
 *
 * Only a PDF page has a print size: `pageWidthMm` is the underlay's printed
 * width (rendered pixels over render density) and `layerWidthUnits` its width
 * in plan units, so `1 unit = pageWidthMm × N / layerWidthUnits` mm. Null for
 * anything that cannot produce a finite positive scale.
 */
export function calibrationFromStatedScale(
    denominator: number,
    pageWidthMm: number,
    layerWidthUnits: number,
): FrameCalibration | null {
    const ok = [denominator, pageWidthMm, layerWidthUnits].every((v) => Number.isFinite(v) && v > 0)
    if (!ok || !Number.isInteger(denominator)) return null
    return {
        mm_per_unit: (pageWidthMm * denominator) / layerWidthUnits,
        method: 'manual',
        stated_scale: denominator,
    }
}

/**
 * True for an automatic scale digitization refuses (spec 2026-09-30-04 §1,
 * the server's `CheckCalibrationTrust`): an `auto` calibration that is not
 * `high` and that nobody accepted — including one written before the job
 * recorded its confidence at all.
 */
export function isUntrustedCalibration(calibration: FrameCalibration | null | undefined): boolean {
    if (!calibration || calibration.method !== 'auto') return false
    return calibration.confidence !== 'high' && !calibration.accepted
}

/** The scale factor a readout multiplies by; `null` for an uncalibrated frame. */
export function mmPerUnit(calibration: FrameCalibration | null | undefined): number | null {
    const v = calibration?.mm_per_unit
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null
}

/**
 * Screen pixels per millimetre on an uncalibrated frame that is not drawn in
 * millimetres: a 100 mm wall is 6 px thick, an 800 mm door 48 px wide, at any
 * zoom.
 */
export const FALLBACK_PX_PER_MM = 0.06

/** Above this extent (in plan units) an uncalibrated plan is read as millimetres. */
export const MM_PLAN_EXTENT = 1000

/** What an uncalibrated frame needs to pick a scale: the zoom and the drawing's size. */
export interface LengthContext {
    /** Screen pixels per plan unit. */
    k?: number
    /** The larger side of the drawing's bounding box, in plan units. */
    extent?: () => number
}

/**
 * The real width an uncalibrated, non-millimetre drawing is assumed to have
 * (20 m, a small building). A 600 mm equipment is then 3% of its width.
 */
export const REFERENCE_BUILDING_MM = 20000

/** A new object may not exceed this fraction of the drawing's smaller side. */
export const MAX_FOOTPRINT_FRACTION = 0.25

/** Caps (of the drawing's smaller side) for a new wall's thickness and a new opening's width. */
export const MAX_THICKNESS_FRACTION = 0.02
export const MAX_OPENING_FRACTION = 0.1

/**
 * Plan units per millimetre — what a millimetre habit ("100 mm walls") is worth
 * as geometry. Calibrated: `1 / mm_per_unit`. Uncalibrated there is nothing to
 * convert with, so the plan is read as millimetres when it is clearly drawn in
 * them (extent over {@link MM_PLAN_EXTENT}); otherwise the drawing is assumed
 * to be {@link REFERENCE_BUILDING_MM} wide, which does not depend on the zoom.
 * Only an empty drawing, with nothing to compare against, is sized on screen
 * ({@link FALLBACK_PX_PER_MM}). Without a context it is 1: units as millimetres.
 */
export function unitsPerMm(
    calibration: FrameCalibration | null | undefined,
    ctx?: LengthContext,
): number {
    const scale = mmPerUnit(calibration)
    if (scale !== null) return 1 / scale
    if (!ctx) return 1
    const extent = ctx.extent ? ctx.extent() : 0
    if (extent > MM_PLAN_EXTENT) return 1
    if (extent > 0) return extent / REFERENCE_BUILDING_MM
    if (!(typeof ctx.k === 'number' && ctx.k > 0)) return 1
    return FALLBACK_PX_PER_MM / ctx.k
}

/**
 * Caps a footprint (plan units) at {@link MAX_FOOTPRINT_FRACTION} of the
 * drawing's smaller side, keeping the width/depth ratio. `side` is that smaller
 * side in plan units; unknown or degenerate leaves the footprint alone.
 */
export function clampFootprint(
    width: number,
    depth: number,
    side: number | null | undefined,
    fraction: number = MAX_FOOTPRINT_FRACTION,
): { width: number; depth: number; clamped: boolean } {
    if (!(typeof side === 'number' && side > 0)) return { width, depth, clamped: false }
    const cap = side * fraction
    const largest = Math.max(width, depth)
    if (!(largest > cap)) return { width, depth, clamped: false }
    const f = cap / largest
    return { width: width * f, depth: depth * f, clamped: true }
}

/** A millimetre length as plan units. */
export const mmToUnits = (mm: number, calibration: FrameCalibration | null | undefined, ctx?: LengthContext): number =>
    mm * unitsPerMm(calibration, ctx)

/** A plan-unit length as millimetres (see {@link unitsPerMm} for the uncalibrated fallback). */
export const unitsToMm = (units: number, calibration: FrameCalibration | null | undefined, ctx?: LengthContext): number =>
    units / unitsPerMm(calibration, ctx)

/** True when the frame carries no usable scale — the "not to scale" badge. */
export const isUncalibrated = (calibration: FrameCalibration | null | undefined): boolean =>
    mmPerUnit(calibration) === null

/**
 * Measure-scale **as a calibration**: two points clicked on the primary plan
 * and the real distance between them.
 *
 * Both points are already in the frame's plan units (the editor works in frame
 * coordinates), so the answer is a pure ratio — and crucially nothing moves,
 * unlike `measureScaleTransform`, which rescales a layer under fixed geometry.
 * A degenerate measurement (coincident points, non-positive distance) yields
 * `null` rather than an infinite or zero scale.
 */
export function calibrationFromMeasure(a: Point, b: Point, realDistanceMm: number): FrameCalibration | null {
    const measured = dist(a, b)
    if (!(measured > 1e-9) || !(realDistanceMm > 0)) return null
    return {
        mm_per_unit: realDistanceMm / measured,
        method: 'measure',
        measure: { a: { ...a }, b: { ...b }, distance_mm: realDistanceMm },
    }
}

/**
 * Anchor pairs **as a calibration**: each pair says "this point of the plan
 * really belongs there". Both ends live in frame plan units, so the fitted
 * similarity's `scale` is exactly the factor the current calibration is off
 * by — the same correction the measure tool computes, from more evidence.
 *
 * One pair carries no scale information (a similarity through a single point
 * is a translation), so it returns `null`: a calibration needs at least two.
 */
export function calibrationFromPairs(
    pairs: AnchorPair[],
    existing?: FrameCalibration | null,
): FrameCalibration | null {
    if (pairs.length < 2) return null
    const fit = solveSimilarity(pairs, { tx: 0, ty: 0, rotation_deg: 0, scale: 1 })
    if (!(fit.scale > 1e-9) || !Number.isFinite(fit.scale)) return null
    const base = mmPerUnit(existing) ?? 1
    return {
        mm_per_unit: base * fit.scale,
        method: 'anchor_pairs',
        pairs: pairs.map((p) => ({ source: { ...p.source }, target: { ...p.target } })),
    }
}

/**
 * The two things "apply" can mean in calibrate mode (spec 2026-08-25-04 §3),
 * kept apart so the shell never guesses:
 *
 * - `calibration` — the target is the frame's **primary plan**: the commit is a
 *   `calibration` PATCH on that registration. Underlay and geometry do not move
 *   relative to each other, by construction.
 * - `placement` — the target is any other layer: the commit is the transform
 *   PATCH it always was, and the UI calls it *placement*, not calibration.
 */
export type CalibrationCommit =
    | { kind: 'calibration'; registrationUid: string; calibration: FrameCalibration | null }
    | { kind: 'placement'; registrationUid: string }

/** Which of the two modes a calibrate target is in. */
export function calibrationModeFor(
    registrationUid: string | null | undefined,
    primaryRegistrationUid: string | null | undefined,
): 'calibration' | 'placement' {
    return registrationUid && registrationUid === primaryRegistrationUid ? 'calibration' : 'placement'
}
